import { HStack, SkeletonText, VStack, Button, Text } from '@cad-arc';
import React from 'react';
import { UseFieldArrayRemove } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { UserSelect } from '../../forms/inputs/user-select';
import { useIntegratedForm } from '../../forms/use-integrated-form';
import { EditUnitFormType, EditUnitResource } from '@mark43/cad-api';

export const UserSelectWithAgencyAndSkills: React.FC<
    Omit<
        React.ComponentProps<typeof UserSelect> & {
            index: number;
            remove: UseFieldArrayRemove;
        },
        'defaultValue'
    >
> = (props): React.ReactElement => {
    const apiClient = useApiClient();
    const { form, getInputProps } = useIntegratedForm<EditUnitFormType>();
    const selectedUserId = props.value?.value;
    const { data: usersAgencyAndSkills, isInitialLoading: isLoadingUserAgencyAndSkills } = useQuery(
        ['user-agency-and-skills', selectedUserId],
        () => {
            if (selectedUserId === undefined) {
                return Promise.resolve(undefined);
            }
            return apiClient
                .request<EditUnitResource.GetEditUnitUsers>({
                    method: 'POST',
                    url: 'edit_unit/users/',
                    data: { userIds: [selectedUserId] },
                })
                .then((resp) => resp.data.data);
        }
    );

    const userAgencyAndSkills = usersAgencyAndSkills?.users[0];

    const personnel = form.watch('personnel');

    return (
        <VStack width="100%" alignItems="start">
            <HStack width="100%">
                <UserSelect
                    {...props}
                    value={props.value}
                    defaultValue={null}
                    {...getInputProps({ isDisabled: props.isDisabled })}
                />
                <Button
                    variant="ghost"
                    leadingVisual="TrashCan"
                    onClick={() => props.remove(props.index)}
                    {...getInputProps({
                        isDisabled: props.isDisabled || personnel.length === 1,
                    })}
                />
            </HStack>
            {userAgencyAndSkills ? (
                <Text variant="caption">
                    {[
                        userAgencyAndSkills.agency,
                        userAgencyAndSkills.userSkills
                            .map((skill) => skill.label)
                            .join(', ')
                            .trim(),
                    ]
                        .filter((text) => !!text)
                        .join(' - ')}
                </Text>
            ) : undefined}
            {isLoadingUserAgencyAndSkills ? <SkeletonText width="240px" /> : undefined}
        </VStack>
    );
};
